<template>
	<div class="flex flex-col gap-0.5 bg-white shadow sm:rounded-lg p-4 m-4">
		<div class="flex justify-between w-full gap-2 mb-4">
			<PageLabel :helpPageName="'SystemConfiguration'">Main Layout</PageLabel>
		</div>

		<!-- Upload Controls Section -->
		<div class="upload-controls-section">
			<div class="header-logo-controls">
				<label class="control-label">Header Logo:</label>
				<input
					type="file"
					ref="fileInputHeaderLogo"
					@change="handleHeaderLogoUpload"
					accept="image/jpeg,image/jpg,image/png,image/gif,image/bmp,image/svg+xml,image/webp,image/tiff"
					style="display: none"
				/>
				<Button @click="triggerHeaderLogoUpload" class="control-btn" size="small">
					<i class="pi pi-upload"></i> {{ headerLogoImage ? "Replace" : "Upload" }}
				</Button>
				<Button @click="removeHeaderLogoImage" class="control-btn remove-btn" v-show="headerLogoImage" size="small">
					<i class="pi pi-trash"></i> Remove
				</Button>
			</div>
		</div>

		<div>
			<div class="main-layout-preview">
				<!-- Main Container with Sidebar and Content -->
				<div class="main-container">
					<!-- Sidebar -->
					<div class="sidebar-preview" :class="{ collapsed: !sidebarPinned }">
						<div class="color-picker-btn-container sidebar-picker">
							<Button
								@click="activateColorPickerForElement('sidebar-preview', $event)"
								size="small"
								severity="secondary"
								:class="['color-picker-btn', { active: activeColorSection === 'sidebar-preview' && showPicker }]"
								title="Change sidebar background color"
							>
								<i class="pi pi-palette"></i>
							</Button>
						</div>
						<div class="sidebar-content">
							<button @click="sidebarPinned = !sidebarPinned" class="menu-toggle">
								<i :class="sidebarPinned ? 'pi pi-times' : 'pi pi-bars'"></i>
							</button>

							<nav class="navigation">
								<button v-for="item in navigationItems" :key="item.name" class="nav-item">
									<i :class="'pi pi-' + item.icon" class="nav-icon"></i>
									<span class="nav-text" :class="{ hide: !sidebarPinned }">{{ item.name }}</span>
								</button>
							</nav>

							<div class="user-section" :class="{ hide: !sidebarPinned }">
								<div class="user-info">
									<a href="#" class="user-name">John Doe</a>
									<a href="#" class="logout-link">Log Out</a>
								</div>
							</div>
						</div>
					</div>

					<!-- Right Content Area -->
					<div class="right-content">
						<!-- Top Header Bar -->
						<div class="logo-area">
							<div class="flex flex-row justify-between px-4 w-full">
								<div class="justify-items-left flex items-center header-logo-wrapper">
									<div class="header-logo-container">
										<div class="logo-content">
											<div v-if="headerLogoImage" v-html="headerLogoImage" class="logo-image-container"></div>
											<img v-else src="/ERM_EcoDesign-Horizontal-White-Header.svg" class="h-full py-1 default-logo" />
											<FontAwesomeIcon :icon="['fa', 'spinner']" class="text-blue-500 ml-2" spin v-show="spinnerHeaderLogo" />
										</div>
									</div>
								</div>

								<div class="flex items-center gap-2">
									<div class="color-picker-btn-container">
										<Button
											@click="activateColorPickerForElement('logo-area', $event)"
											size="small"
											severity="secondary"
											:class="['color-picker-btn', { active: activeColorSection === 'logo-area' && showPicker }]"
											title="Change header background color"
										>
											<i class="pi pi-palette"></i>
										</Button>
									</div>
									<Button size="small">How to use this tool?</Button>
									<Button size="small">Help & Support</Button>
									<div class="justify-items-right flex flex-row gap-1">
										<Button size="small">
											<i class="pi pi-user"></i>
											User Name
											<i class="pi pi-chevron-down"></i>
										</Button>
									</div>
								</div>
							</div>
						</div>

						<!-- Content Area -->
						<div class="content-preview">
							<div class="breadcrumb-area">
								<div class="color-picker-btn-container breadcrumb-picker">
									<Button
										@click="activateColorPickerForElement('breadcrumb-area', $event)"
										size="small"
										severity="secondary"
										:class="['color-picker-btn', { active: activeColorSection === 'breadcrumb-area' && showPicker }]"
										title="Change breadcrumb background color"
									>
										<i class="pi pi-palette"></i>
									</Button>
								</div>
								<span>
									<i class="pi pi-home" :style="{ color: breadcrumbIconColor + ' !important' }"></i>
									Home
									<span class="breadcrumb-separator" :style="{ color: breadcrumbTextColor + ' !important' }">></span>
									System Configuration
								</span>
							</div>
							<div class="page-content">
								<h2>Page Content Area</h2>
								<p>No changes can be made to this part of the screen.</p>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>

		<!-- Color Picker Panel - using same structure as Components file -->
		<div v-if="showPicker" :style="{ position: 'fixed', top: pickerY + 'px', left: pickerX + 'px', zIndex: 9999 }" class="color-picker-panel">
			<div class="color-picker-header">
				<span class="color-picker-title">Edit Color</span>
				<button @click="closePicker" class="color-picker-close">×</button>
			</div>

			<!-- Mode switching tabs -->
			<div class="mode-tabs">
				<Button @click="switchColorMode('background', $event)" :class="['mode-tab', { active: colorMode === 'background' }]" size="small">
					Background
				</Button>
				<Button
					v-if="activeColorSection === 'logo-area'"
					@click="switchColorMode('underHeader', $event)"
					:class="['mode-tab', { active: colorMode === 'underHeader' }]"
					size="small"
				>
					Under Header
				</Button>
				<Button @click="switchColorMode('text', $event)" :class="['mode-tab', { active: colorMode === 'text' }]" size="small">
					<i class="pi pi-font"></i> Text
				</Button>
				<Button
					v-if="activeColorSection === 'sidebar-preview' || activeColorSection === 'sidebar-content' || activeColorSection === 'logo-area'"
					@click="switchColorMode('buttonColor', $event)"
					:class="['mode-tab', { active: colorMode === 'buttonColor' }]"
					size="small"
				>
					<i class="pi pi-circle-fill"></i> Button Color
				</Button>
			</div>

			<!-- Auto Contrast Checkbox -->
			<div v-if="colorMode === 'background'" class="auto-contrast-section">
				<div class="flex items-center gap-2 p-2 border-t border-gray-200">
					<Checkbox v-model="autoContrast" inputId="autoContrast" binary />
					<label for="autoContrast" class="text-sm text-gray-700 cursor-pointer"> Auto contrast </label>
					<i
						class="pi pi-question-circle text-gray-400 cursor-help"
						title="Automatically adjusts text color (black/white) based on background brightness"
					></i>
				</div>
			</div>

			<div class="color-picker-content">
				<ColorPicker
					v-if="colorMode === 'background' || colorMode === 'underHeader'"
					v-model="selectedColor"
					@update:modelValue="onColorPickerChange"
					inline
					ref="colorPickerRef"
					class="color-picker"
				/>
				<ColorPicker v-else v-model="selectedTextColor" @update:modelValue="onColorPickerChange" inline ref="colorPickerRef" class="color-picker" />
				<div class="color-palette-miniatures" v-if="paletteColors">
					<div class="palette-grid">
						<!-- Primary, Secondary, Accent with border -->
						<div class="primary-colors-box">
							<div class="palette-row primary-row">
								<div
									v-for="(color, index) in getPrimaryColors()"
									:key="index"
									class="palette-swatch primary-swatch"
									:style="{ backgroundColor: color.value }"
									@click="selectPaletteColor(color.value)"
									:title="color.name"
								></div>
							</div>
						</div>
						<!-- Other colors -->
						<div class="palette-row">
							<div
								v-for="(color, index) in getOtherColors().slice(0, 3)"
								:key="index"
								class="palette-swatch"
								:style="{ backgroundColor: color.value }"
								@click="selectPaletteColor(color.value)"
							></div>
						</div>
						<div class="palette-row">
							<div
								v-for="(color, index) in getOtherColors().slice(3, 6)"
								:key="index"
								class="palette-swatch"
								:style="{ backgroundColor: color.value }"
								@click="selectPaletteColor(color.value)"
							></div>
						</div>
						<div class="palette-row">
							<div
								v-for="(color, index) in getOtherColors().slice(6, 9)"
								:key="index"
								class="palette-swatch"
								:style="{ backgroundColor: color.value }"
								@click="selectPaletteColor(color.value)"
							></div>
						</div>
					</div>
				</div>
			</div>
			<div class="hex-input-container">
				<label for="hexInput" class="hex-input-label">Hex:</label>
				<InputText
					id="hexInput"
					v-model="hexInputValue"
					@input="onHexInputChange"
					@blur="validateAndApplyHex"
					class="hex-input"
					placeholder="#ffffff"
					maxlength="7"
				/>
				<Button @click="sendColorToParent" size="small" class="send-color-btn" title="Use it to generate color scheme">
					<i class="pi pi-arrow-up"></i>
				</Button>
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
import { ref, onMounted, nextTick, watch, computed } from "vue"
import {
	ThemeState,
	ThemeTemplate,
	ThemeMgmClient,
	TemplateUI,
	Components,
	Breadcrumb,
	ColorSchemeBreadcrumb,
	BreadcrumbState,
	BreadcrumbRoot,
	BreadcrumbItem,
	BreadcrumbIcon,
} from "../../../entities/NSwagGenerated"
import ColorPicker from "primevue/colorpicker"
import InputText from "primevue/inputtext"
import Button from "primevue/button"
import Checkbox from "primevue/checkbox"
import PageLabel from "../pageLabel.vue"
import { library } from "@fortawesome/fontawesome-svg-core"
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome"
import { faTriangleExclamation, faSpinner, faBan } from "@fortawesome/free-solid-svg-icons"
library.add(faTriangleExclamation, faSpinner, faBan)

const props = defineProps<{
	selectedTemplate?: ThemeState
	selectedColor?: string
	paletteColors?: Record<string, string>
}>()

const emit = defineEmits<{
	(e: "updateTheme", themeData: ThemeState): void
	(e: "colorSelected", color: string): void
	(e: "disablePasteColor"): void
	(e: "saveToUndo"): void
}>()

// Functions for palette colors
const getPrimaryColors = () => {
	if (!props.paletteColors) return []
	return [
		{ name: "Primary", value: props.paletteColors["0"] },
		{ name: "Secondary", value: props.paletteColors["1"] },
		{ name: "Accent", value: props.paletteColors["2"] },
	]
}

const getOtherColors = () => {
	if (!props.paletteColors) return []
	const colors = []
	for (let i = 3; i < 12; i++) {
		if (props.paletteColors[i.toString()]) {
			colors.push({ name: "", value: props.paletteColors[i.toString()] })
		}
	}
	return colors
}

const selectPaletteColor = (color: string) => {
	// Set the appropriate color based on current mode
	if (colorMode.value === "text") {
		selectedTextColor.value = color
	} else {
		selectedColor.value = color
	}

	// Update the hex input
	hexInputValue.value = color.toUpperCase()

	// Apply the color change
	onColorPickerChange(color)
}

const localThemeSettings = ref<ThemeTemplate>(new ThemeTemplate())
const localThemeState = ref<ThemeState>(new ThemeState())
const showPicker = ref<boolean>(false)
const activeColorSection = ref<string | null>(null)
const selectedColor = ref<string>("")
const selectedTextColor = ref<string>("")
const hexInputValue = ref<string>("")
const pickerX = ref<number>(0)
const pickerY = ref<number>(0)
const colorMode = ref<"background" | "text" | "underHeader" | "buttonColor">("background")
const autoContrast = ref<boolean>(true) // Auto-contrast enabled by default
const colorPickerRef = ref<InstanceType<typeof ColorPicker> | null>(null)
const sidebarPinned = ref<boolean>(true)
const fileInputHeaderLogo = ref<HTMLInputElement | null>(null)
const headerLogoImage = ref<string | null>(null)
const spinnerHeaderLogo = ref<boolean>(false)

let clickedElement: HTMLElement | null = null

// const themeMgmClient = new ThemeMgmClient(import.meta.env.VITE_API_URL?.toString())

// Computed properties for breadcrumb colors
const breadcrumbTextColor = computed(() => {
	const color = localThemeState.value.selectedTheme?.components?.breadcrumb?.colorScheme?.light?.item?.color || "#333333"
	console.log("🍞 breadcrumbTextColor computed:", color)
	return color
})

const breadcrumbIconColor = computed(() => {
	const color =
		localThemeState.value.selectedTheme?.components?.breadcrumb?.colorScheme?.light?.item?.icon?.color ||
		localThemeState.value.selectedTheme?.components?.breadcrumb?.colorScheme?.light?.item?.color ||
		"#333333"
	console.log("🍞 breadcrumbIconColor computed:", color)
	return color
})

// State for pending image uploads
const pendingUploads = ref<{
	headerLogoFile: File | null
}>({
	headerLogoFile: null,
})

// Function to save pending image uploads
const savePendingImages = async (): Promise<void> => {
	const promises: Promise<void>[] = []

	// Check if theme ID is available
	if (!localThemeState.value.id) {
		throw new Error("Theme not found. Cannot save images without a theme selected.")
	}

	// Upload header logo if pending
	if (pendingUploads.value.headerLogoFile) {
		promises.push(
			uploadBinaryImage(pendingUploads.value.headerLogoFile, "headerlogo", localThemeState.value.id).then(() => {
				pendingUploads.value.headerLogoFile = null
			})
		)
	}

	// Wait for all uploads to complete
	if (promises.length > 0) {
		await Promise.all(promises)
	}
}

// Function to upload binary image
const uploadBinaryImage = async (file: File, imageType: "headerlogo", themeId: string): Promise<any> => {
	const formData = new FormData()
	formData.append("file", file)
	formData.append("imageType", imageType)
	formData.append("themeId", themeId)

	try {
		const response = await fetch(`${import.meta.env.VITE_API_URL}/api/themeMgm/uploadImage`, {
			method: "POST",
			body: formData,
		})

		if (!response.ok) {
			throw new Error(`Upload failed: ${response.statusText}`)
		}

		return await response.json()
	} catch (error) {
		console.error(`Error uploading ${imageType} image:`, error)
		throw error
	}
}

// Expose the function to parent component
defineExpose({
	savePendingImages,
})

// Function to create image HTML from binary data
const createImageFromBinary = (binaryData: string, contentType: string): string => {
	if (!binaryData) return ""

	// Check if binaryData is already a data URL
	if (binaryData.startsWith("data:")) {
		return `<img src="${binaryData}" alt="Image" style="max-width: 100%; max-height: 60px; height: auto; width: auto; object-fit: contain; background: transparent;" class="transparent-image header-logo-img" />`
	}

	// Handle SVG files specially
	if (contentType === "image/svg+xml" || contentType.includes("svg")) {
		// For SVG, try to decode as text first
		try {
			const svgContent = atob(binaryData)
			// If it's valid SVG content, return it with size constraints
			if (svgContent.includes("<svg")) {
				// Add viewBox and size constraints to SVG
				const modifiedSvg = svgContent.replace(
					/<svg([^>]*)>/,
					'<svg$1 style="max-width: 100%; max-height: 60px; height: auto; width: auto; object-fit: contain;" class="header-logo-svg">'
				)
				return modifiedSvg
			}
		} catch (error) {
			// If decoding fails, fall back to data URL
			console.log("SVG decode failed, using data URL:", error)
		}
	}

	// Check if binaryData is already base64 encoded
	let base64Data = binaryData
	if (!binaryData.startsWith("/9j") && !binaryData.startsWith("iVBOR") && !binaryData.startsWith("R0lGOD") && !binaryData.startsWith("PHN2Zw")) {
		// If not base64, assume it's already binary and convert to base64
		try {
			base64Data = btoa(binaryData)
		} catch (error) {
			console.error("Error encoding binary data to base64:", error)
			return ""
		}
	}

	const dataUrl = `data:${contentType};base64,${base64Data}`
	return `<img src="${dataUrl}" alt="Image" style="max-width: 100%; max-height: 60px; height: auto; width: auto; object-fit: contain; background: transparent;" class="transparent-image header-logo-img" />`
}

const navigationItems = [
	{ name: "Dashboard", icon: "chart-line", current: true },
	{ name: "System Configuration", icon: "cog", current: false },
	{ name: "Projects", icon: "briefcase", current: false },
]

onMounted(() => {})

const applyTemplate = async (selectedTemplate: ThemeState) => {
	// Set localThemeState first
	localThemeState.value = selectedTemplate

	// Then set localThemeSettings to reference the same object
	localThemeSettings.value = localThemeState.value.selectedTheme!

	console.log("🍞 After applyTemplate assignment:")
	console.log("🍞   localThemeSettings === localThemeState.selectedTheme?", localThemeSettings.value === localThemeState.value.selectedTheme)
	console.log(
		"🍞   localThemeSettings.semantic === localThemeState.selectedTheme.semantic?",
		localThemeSettings.value.semantic === localThemeState.value.selectedTheme?.semantic
	)

	// Ensure templateUI is initialized
	if (!selectedTemplate.templateUI) {
		selectedTemplate.templateUI = new TemplateUI()
	}

	// IMPORTANT: Do NOT reassign semantic or any sub-objects here!
	// localThemeSettings.value and localThemeState.value.selectedTheme are the SAME object
	// Any reassignment will break the reference and cause data loss

	// Handle header logo image binary data
	if (selectedTemplate.templateUI?.menulogoImageBinary) {
		const logoContentType = selectedTemplate.templateUI.menulogoImageContentType || "image/png"
		headerLogoImage.value = createImageFromBinary(selectedTemplate.templateUI.menulogoImageBinary, logoContentType)
	} else {
		headerLogoImage.value = headerLogoImage.value
	}

	// Ensure transparency is preserved for existing images
	if (headerLogoImage.value && headerLogoImage.value.includes("<img")) {
		headerLogoImage.value = headerLogoImage.value.replace(
			/<img([^>]*?)style="([^"]*?)"([^>]*?)>/g,
			'<img$1style="$2; background: transparent !important;" class="transparent-image"$3>'
		)
		// Add class if not present
		if (!headerLogoImage.value.includes('class="transparent-image"')) {
			headerLogoImage.value = headerLogoImage.value.replace(/<img([^>]*?)>/g, '<img$1 class="transparent-image">')
		}
	}

	// Force transparency handling after DOM update
	nextTick(() => {
		const images = document.querySelectorAll(".logo img, .header-logo-container img")
		images.forEach((img) => {
			const htmlImg = img as HTMLImageElement
			htmlImg.style.background = "transparent"
			htmlImg.style.backgroundColor = "transparent"
			// Ensure container transparency
			if (htmlImg.parentElement) {
				htmlImg.parentElement.style.background = "transparent"
				htmlImg.parentElement.style.backgroundColor = "transparent"
			}
		})
	})

	nextTick(() => {
		// Reset inline styles applied by the color picker in the preview area
		const previewArea = document.querySelector(".main-layout-preview")
		if (previewArea) {
			// Reset background colors
			const elementsWithBackgroundStyles = previewArea.querySelectorAll('[style*="background-color"]')
			elementsWithBackgroundStyles.forEach((element) => {
				const htmlElement = element as HTMLElement
				htmlElement.style.removeProperty("background-color")
			})

			// Reset text colors in sidebar elements specifically
			const sidebarElements = previewArea.querySelectorAll(".sidebar-preview, .nav-item, .menu-toggle, .user-name, .logout-link")

			sidebarElements.forEach((element) => {
				const htmlElement = element as HTMLElement
				const oldColor = htmlElement.style.color

				htmlElement.style.removeProperty("color")
			})
		}

		// Apply theme variables to main layout elements
		const sidebar = localThemeSettings.value.semantic?.sidebar as any
		const menuUp = localThemeSettings.value.semantic?.menuUp as any

		// Auto-calculate text colors for buttons based on button background color
		const headerButtonTextColor = menuUp?.buttonTextColor || (menuUp?.buttonColor ? (isColorLight(menuUp.buttonColor) ? "#000000" : "#ffffff") : "white")
		const sidebarButtonTextColor = sidebar?.buttonTextColor || (sidebar?.buttonColor ? (isColorLight(sidebar.buttonColor) ? "#000000" : "#ffffff") : "#ffffff")

		const styleVars = {
			"--main-sidebar-background-color": localThemeSettings.value.semantic?.sidebar?.backgroundColor || "#1a1a2e",
			"--main-header-background-color": localThemeSettings.value.semantic?.menuUp?.backgroundColor || "#f8f9fa",
			"--main-header-text-color": localThemeSettings.value.semantic?.menuUp?.textColor || "#333333",
			"--main-header-borderbottom-color": localThemeSettings.value.semantic?.menuUp?.borderBottom || "#333333",
			"--main-header-button-color": menuUp?.buttonColor || "#007bff",
			"--main-header-button-text-color": headerButtonTextColor,
			"--main-sidebar-text-color":
				localThemeSettings.value.semantic?.sidebar?.textColor || localThemeState.value.selectedTheme?.semantic?.sidebar?.textColor || "#ffffff",
			"--main-sidebar-button-color": sidebar?.buttonColor || "transparent",
			"--main-sidebar-button-text-color": sidebarButtonTextColor,
			"--main-breadcrumb-background-color":
				localThemeState.value.selectedTheme?.components?.breadcrumb?.colorScheme?.light?.root?.background || "#ffffff",
			"--main-breadcrumb-text-color": localThemeState.value.selectedTheme?.components?.breadcrumb?.colorScheme?.light?.item?.color || "#333333",
			"--main-breadcrumb-icon-color":
				localThemeState.value.selectedTheme?.components?.breadcrumb?.colorScheme?.light?.item?.icon?.color ||
				localThemeState.value.selectedTheme?.components?.breadcrumb?.colorScheme?.light?.item?.color ||
				"#333333",
		}

		// Apply CSS variables only to the preview area, not globally
		const mainPreviewArea = document.querySelector(".main-layout-preview") as HTMLElement
		if (mainPreviewArea) {
			Object.entries(styleVars).forEach(([key, value]) => {
				mainPreviewArea.style.setProperty(key, value || "#ffffff")
			})

			// Debug: Verify the CSS variable is actually set in preview
			const appliedTextColor = getComputedStyle(mainPreviewArea).getPropertyValue("--main-sidebar-text-color")
		}
	})
}

// Function to activate color picker for a specific element
const activateColorPickerForElement = (elementType: string, event: Event) => {
	// Prevent event propagation
	event.stopPropagation()

	activeColorSection.value = elementType

	// Find the element to get its current color
	let targetElement: HTMLElement | null = null
	let currentColor = "#ffffff" // default color

	switch (elementType) {
		case "logo-area":
			targetElement = document.querySelector(".logo-area") as HTMLElement
			break
		case "sidebar-preview":
			targetElement = document.querySelector(".sidebar-preview") as HTMLElement
			break
		case "breadcrumb-area":
			targetElement = document.querySelector(".breadcrumb-area") as HTMLElement
			break
	}

	if (targetElement) {
		// Get current background color
		const computedStyle = window.getComputedStyle(targetElement)
		const bgColor = computedStyle.backgroundColor

		if (bgColor && bgColor !== "rgba(0, 0, 0, 0)" && bgColor !== "transparent") {
			currentColor = rgbToHex(bgColor)
		} else {
			// Use contextual default colors
			switch (elementType) {
				case "logo-area":
					currentColor = "rgb(248, 249, 250)" // Header background
					break
				case "sidebar-preview":
					currentColor = "rgb(26, 26, 46)" // Sidebar background
					break
				case "breadcrumb-area":
					currentColor = "rgb(255, 255, 255)" // Breadcrumb background (white)
					break
				default:
					currentColor = "rgb(255, 255, 255)" // Default white
			}
			currentColor = rgbToHex(currentColor)
		}

		// Set up the color picker
		clickedElement = targetElement
		selectedColor.value = currentColor
		hexInputValue.value = currentColor

		// Position the color picker to the left of the button that was clicked
		const buttonRect = (event.target as HTMLElement).getBoundingClientRect()
		pickerX.value = buttonRect.left - 290 // 280px picker width + 10px margin
		pickerY.value = buttonRect.top + window.scrollY

		// Show the color picker
		showPicker.value = true

		// Set default color mode to background
		colorMode.value = "background"

		// Add event listener for clicks outside
		nextTick(() => {
			if (colorPickerRef && colorPickerRef.value) {
				document.addEventListener("click", handleClickOutside)
			}
		})
	}
}

const handleClickOutside = (event: MouseEvent) => {
	const target = event.target as HTMLElement

	// Don't close if clicking on color picker elements
	if (target.classList.contains("p-colorpicker-color-handle") || target.classList.contains("p-colorpicker-color")) {
		return
	}

	// Don't close if clicking on mode tabs or color picker panel
	if (target.closest(".mode-tabs") || target.closest(".color-picker-panel")) {
		return
	}

	closePicker()
}

const closePicker = () => {
	showPicker.value = false
	clickedElement = null
	activeColorSection.value = null
	document.removeEventListener("click", handleClickOutside)
	emit("saveToUndo")
}

const switchColorMode = (mode: "background" | "text" | "underHeader" | "buttonColor", event?: Event) => {
	event?.stopPropagation()
	colorMode.value = mode

	// When switching to text or buttonColor mode, sync with current clicked element's text color
	if ((mode === "text" || mode === "buttonColor") && clickedElement) {
		const computedStyle = window.getComputedStyle(clickedElement)
		const currentTextColor = computedStyle.color
		if (currentTextColor && currentTextColor !== "rgba(0, 0, 0, 0)") {
			const hexColor = rgbToHex(currentTextColor)
			selectedTextColor.value = hexColor
			hexInputValue.value = hexColor
		}
	} else if (mode === "background" && clickedElement) {
		// When switching to background mode, sync with current element's background color
		const computedStyle = window.getComputedStyle(clickedElement)
		const currentBgColor = computedStyle.backgroundColor
		if (currentBgColor && currentBgColor !== "rgba(0, 0, 0, 0)") {
			const hexColor = rgbToHex(currentBgColor)
			selectedColor.value = hexColor
			hexInputValue.value = hexColor
		}
	} else if (mode === "underHeader" && clickedElement) {
		// When switching to underHeader mode, sync with current element's border bottom color
		const currentBorderColor = localThemeSettings.value.semantic?.menuUp?.borderBottom || "#333333"
		const hexColor = rgbToHex(currentBorderColor)
		selectedColor.value = hexColor
		hexInputValue.value = hexColor
	}
}

const onColorPickerChange = (value: string) => {
	if (colorMode.value === "background") {
		selectedColor.value = value
	} else {
		selectedTextColor.value = value
	}

	// Update hex input
	hexInputValue.value = value.toUpperCase()

	// Apply auto contrast if enabled for background mode
	if (autoContrast.value && colorMode.value === "background" && clickedElement) {
		const textColor = isColorLight(value) ? "#000000" : "#ffffff"
		selectedTextColor.value = textColor
		// Auto-apply text color for better contrast
		const originalMode = colorMode.value
		colorMode.value = "text"

		// Apply text color to the element
		clickedElement.style.color = textColor
		updateLocalThemeSettings(Array.from(clickedElement.classList), textColor, "text")

		// Switch back to original mode
		colorMode.value = originalMode
	}

	// Apply auto contrast for button color mode
	if (colorMode.value === "buttonColor" && clickedElement) {
		const textColor = isColorLight(value) ? "#000000" : "#ffffff"

		// Apply text color to buttons
		const previewArea = document.querySelector(".main-layout-preview")
		if (previewArea) {
			if (activeColorSection.value === "logo-area") {
				// Header buttons
				const headerButtons = previewArea.querySelectorAll(".logo-area button:not(.color-picker-btn)")
				headerButtons.forEach((element) => {
					const htmlElement = element as HTMLElement
					htmlElement.style.setProperty("color", textColor, "important")
				})
			} else if (activeColorSection.value === "sidebar-preview" || activeColorSection.value === "sidebar-content") {
				// Sidebar buttons
				const sidebarButtons = previewArea.querySelectorAll(".nav-item")
				sidebarButtons.forEach((element) => {
					const htmlElement = element as HTMLElement
					htmlElement.style.setProperty("color", textColor, "important")
				})
			}
		}
	}

	// Apply the main color change
	applyColor()
}

// Helper function to check if a color is light or dark
const isColorLight = (color: string): boolean => {
	// Remove # if present
	const hex = color.replace("#", "")

	// Convert hex to RGB
	const r = parseInt(hex.substring(0, 2), 16)
	const g = parseInt(hex.substring(2, 4), 16)
	const b = parseInt(hex.substring(4, 6), 16)

	// Calculate luminance using the formula: (0.299*R + 0.587*G + 0.114*B)
	const luminance = 0.299 * r + 0.587 * g + 0.114 * b
	// Return true if luminance > 128 (light), false if <= 128 (dark)
	return luminance > 128
}

const applyColor = () => {
	let color: string
	let mode: "background" | "text" | "underHeader" | "buttonColor" = "background"

	if (colorMode.value === "text") {
		color = selectedTextColor.value.startsWith("#") ? selectedTextColor.value : "#" + selectedTextColor.value
		mode = "text"
	} else if (colorMode.value === "buttonColor") {
		color = selectedTextColor.value.startsWith("#") ? selectedTextColor.value : "#" + selectedTextColor.value
		mode = "buttonColor"
	} else if (colorMode.value === "underHeader") {
		color = selectedColor.value.startsWith("#") ? selectedColor.value : "#" + selectedColor.value
		mode = "underHeader"
	} else {
		color = selectedColor.value.startsWith("#") ? selectedColor.value : "#" + selectedColor.value
	}

	if (!color || !/^#[0-9A-Fa-f]{6}$/.test(color)) {
		return
	}

	if (clickedElement) {
		// Save current color state to undo
		emit("saveToUndo")
		// Check if the clicked element is within the preview area
		const isInPreview = clickedElement.closest(".main-layout-preview")
		if (isInPreview) {
			if (mode === "text") {
				clickedElement.style.setProperty("color", color, "important")
			} else if (mode === "underHeader") {
				// Apply border-bottom color to the logo-area element
				const logoArea = document.querySelector(".main-layout-preview .logo-area") as HTMLElement
				if (logoArea) {
					logoArea.style.setProperty("border-bottom-color", color, "important")
				}
			} else if (mode === "buttonColor") {
				clickedElement.style.setProperty("background-color", color, "important")
				clickedElement.style.setProperty("border-color", color, "important")
			} else {
				clickedElement.style.setProperty("background-color", color, "important")
			}
		}
		const classNames = Array.from(clickedElement.classList)
		updateLocalThemeSettings(classNames, color, mode)
	}
}

const updateLocalThemeSettings = (classes: string[], color: string, mode: "background" | "text" | "underHeader" | "buttonColor" = "background") => {
	const colorMappings: { [key: string]: () => void } = {
		"sidebar-preview": () => {
			if (!localThemeState.value.selectedTheme!.semantic!.sidebar) {
				localThemeState.value.selectedTheme!.semantic!.sidebar = { backgroundColor: "", textColor: "", init: () => {}, toJSON: () => ({}) } as any
			}
			if (mode === "text") {
				localThemeState.value.selectedTheme!.semantic!.sidebar!.textColor = color
				// Apply text color directly to sidebar elements in preview
				const previewArea = document.querySelector(".main-layout-preview")
				if (previewArea) {
					const sidebarTextElements = previewArea.querySelectorAll(".nav-item, .menu-toggle, .user-name, .logout-link")
					sidebarTextElements.forEach((element) => {
						const htmlElement = element as HTMLElement
						htmlElement.style.setProperty("color", color, "important")
					})
				}
			} else if (mode === "buttonColor") {
				// Add buttonColor field to sidebar (extends the interface)
				const sidebarAny = localThemeState.value.selectedTheme!.semantic!.sidebar as any
				sidebarAny.buttonColor = color

				// Calculate and save text color for buttons based on background color
				const textColor = isColorLight(color) ? "#000000" : "#ffffff"
				sidebarAny.buttonTextColor = textColor

				// Apply button color and text color to sidebar buttons in preview
				const previewArea = document.querySelector(".main-layout-preview")
				if (previewArea) {
					const sidebarButtons = previewArea.querySelectorAll(".nav-item")
					sidebarButtons.forEach((element) => {
						const htmlElement = element as HTMLElement
						htmlElement.style.setProperty("background-color", color, "important")
						htmlElement.style.setProperty("color", textColor, "important")
					})
				}
			} else {
				localThemeState.value.selectedTheme!.semantic!.sidebar!.backgroundColor = color
				// Apply background color to sidebar in preview
				const previewArea = document.querySelector(".main-layout-preview")
				if (previewArea) {
					const sidebarElement = previewArea.querySelector(".sidebar-preview") as HTMLElement
					if (sidebarElement) {
						sidebarElement.style.setProperty("background-color", color, "important")
					}
				}
			}
		},
		"breadcrumb-area": () => {
			// Initialize components.breadcrumb structure if it doesn't exist using NSwag classes
			if (!localThemeState.value.selectedTheme!.components) {
				localThemeState.value.selectedTheme!.components = new Components()
			}
			if (!localThemeState.value.selectedTheme!.components!.breadcrumb) {
				localThemeState.value.selectedTheme!.components!.breadcrumb = new Breadcrumb()
				localThemeState.value.selectedTheme!.components!.breadcrumb!.colorScheme = new ColorSchemeBreadcrumb()
				localThemeState.value.selectedTheme!.components!.breadcrumb!.colorScheme!.light = new BreadcrumbState()
				localThemeState.value.selectedTheme!.components!.breadcrumb!.colorScheme!.light!.root = new BreadcrumbRoot()
				localThemeState.value.selectedTheme!.components!.breadcrumb!.colorScheme!.light!.item = new BreadcrumbItem()
			}
			if (!localThemeState.value.selectedTheme!.components!.breadcrumb!.colorScheme) {
				localThemeState.value.selectedTheme!.components!.breadcrumb!.colorScheme = new ColorSchemeBreadcrumb()
				localThemeState.value.selectedTheme!.components!.breadcrumb!.colorScheme!.light = new BreadcrumbState()
				localThemeState.value.selectedTheme!.components!.breadcrumb!.colorScheme!.light!.root = new BreadcrumbRoot()
				localThemeState.value.selectedTheme!.components!.breadcrumb!.colorScheme!.light!.item = new BreadcrumbItem()
			}
			if (!localThemeState.value.selectedTheme!.components!.breadcrumb!.colorScheme!.light) {
				localThemeState.value.selectedTheme!.components!.breadcrumb!.colorScheme!.light = new BreadcrumbState()
				localThemeState.value.selectedTheme!.components!.breadcrumb!.colorScheme!.light!.root = new BreadcrumbRoot()
				localThemeState.value.selectedTheme!.components!.breadcrumb!.colorScheme!.light!.item = new BreadcrumbItem()
			}
			if (!localThemeState.value.selectedTheme!.components!.breadcrumb!.colorScheme!.light!.root) {
				localThemeState.value.selectedTheme!.components!.breadcrumb!.colorScheme!.light!.root = new BreadcrumbRoot()
			}
			if (!localThemeState.value.selectedTheme!.components!.breadcrumb!.colorScheme!.light!.item) {
				localThemeState.value.selectedTheme!.components!.breadcrumb!.colorScheme!.light!.item = new BreadcrumbItem()
			}

			// Update the color in the correct location
			if (mode === "text") {
				// Set text color
				localThemeState.value.selectedTheme!.components!.breadcrumb!.colorScheme!.light!.item!.color = color

				// Initialize and set icon color to match text color
				if (!localThemeState.value.selectedTheme!.components!.breadcrumb!.colorScheme!.light!.item!.icon) {
					localThemeState.value.selectedTheme!.components!.breadcrumb!.colorScheme!.light!.item!.icon = new BreadcrumbIcon()
				}
				localThemeState.value.selectedTheme!.components!.breadcrumb!.colorScheme!.light!.item!.icon!.color = color

				// Apply CSS variables for both text and icon
				document.documentElement.style.setProperty("--main-breadcrumb-text-color", color)
				document.documentElement.style.setProperty("--main-breadcrumb-icon-color", color)
				console.log("🍞 Set breadcrumb text color and icon color:", color)
			} else {
				localThemeState.value.selectedTheme!.components!.breadcrumb!.colorScheme!.light!.root!.background = color
				document.documentElement.style.setProperty("--main-breadcrumb-background-color", color)
				console.log("🍞 Set breadcrumb background color:", color)
			}
			console.log("🍞 Full breadcrumb object:", localThemeState.value.selectedTheme!.components!.breadcrumb)
		},
		"sidebar-content": () => {
			if (!localThemeState.value.selectedTheme!.semantic!.sidebar) {
				localThemeState.value.selectedTheme!.semantic!.sidebar = { backgroundColor: "", textColor: "", init: () => {}, toJSON: () => ({}) } as any
			}
			if (mode === "text") {
				localThemeState.value.selectedTheme!.semantic!.sidebar!.textColor = color
				// Apply text color directly to sidebar elements in preview
				const previewArea = document.querySelector(".main-layout-preview")
				if (previewArea) {
					const sidebarTextElements = previewArea.querySelectorAll(".nav-item, .menu-toggle, .user-name, .logout-link")
					sidebarTextElements.forEach((element) => {
						const htmlElement = element as HTMLElement
						htmlElement.style.setProperty("color", color, "important")
					})
				}
			} else if (mode === "buttonColor") {
				// Add buttonColor field to sidebar (extends the interface)
				const sidebarAny = localThemeState.value.selectedTheme!.semantic!.sidebar as any
				sidebarAny.buttonColor = color

				// Calculate and save text color for buttons based on background color
				const textColor = isColorLight(color) ? "#000000" : "#ffffff"
				sidebarAny.buttonTextColor = textColor

				// Apply button color and text color to sidebar buttons in preview
				const previewArea = document.querySelector(".main-layout-preview")
				if (previewArea) {
					const sidebarButtons = previewArea.querySelectorAll(".nav-item")
					sidebarButtons.forEach((element) => {
						const htmlElement = element as HTMLElement
						htmlElement.style.setProperty("background-color", color, "important")
						htmlElement.style.setProperty("color", textColor, "important")
					})
				}
			} else {
				localThemeState.value.selectedTheme!.semantic!.sidebar!.backgroundColor = color
				// Apply background color to sidebar in preview
				const previewArea = document.querySelector(".main-layout-preview")
				if (previewArea) {
					const sidebarElement = previewArea.querySelector(".sidebar-preview") as HTMLElement
					if (sidebarElement) {
						sidebarElement.style.setProperty("background-color", color, "important")
					}
				}
			}
		},
		"nav-item": () => {
			if (!localThemeState.value.selectedTheme!.semantic!.sidebar) {
				localThemeState.value.selectedTheme!.semantic!.sidebar = { backgroundColor: "", textColor: "", init: () => {}, toJSON: () => ({}) }
			}
			localThemeState.value.selectedTheme!.semantic!.sidebar!.textColor = color
			// Apply text color directly to sidebar elements in preview
			const previewArea = document.querySelector(".main-layout-preview")
			if (previewArea) {
				const sidebarTextElements = previewArea.querySelectorAll(".nav-item, .menu-toggle, .user-name, .logout-link")
				sidebarTextElements.forEach((element) => {
					const htmlElement = element as HTMLElement
					htmlElement.style.setProperty("color", color, "important")
				})
			}
		},
		"header-button": () => {
			const semantic = localThemeState.value.selectedTheme!.semantic as any
			if (!semantic.header) {
				semantic.header = { backgroundColor: "", textColor: "" }
			}
			semantic.header.textColor = color
			document.documentElement.style.setProperty("--main-header-text-color", color)
		},
		"logo-area": () => {
			const semantic = localThemeState.value.selectedTheme!.semantic as any
			const localSemantic = localThemeSettings.value.semantic as any
			if (!semantic.menuUp) {
				semantic.menuUp = { backgroundColor: "", textColor: "", borderBottom: "", buttonColor: "" }
			}
			if (!localSemantic.menuUp) {
				localSemantic.menuUp = { backgroundColor: "", textColor: "", borderBottom: "", buttonColor: "" }
			}
			if (mode === "text") {
				semantic.menuUp.textColor = color
				localSemantic.menuUp.textColor = color
				document.documentElement.style.setProperty("--main-header-text-color", color)
			} else if (mode === "buttonColor") {
				semantic.menuUp.buttonColor = color
				localSemantic.menuUp.buttonColor = color

				// Calculate and save text color for buttons
				const textColor = isColorLight(color) ? "#000000" : "#ffffff"
				const menuUpAny = semantic.menuUp as any
				menuUpAny.buttonTextColor = textColor
				const localMenuUpAny = localSemantic.menuUp as any
				localMenuUpAny.buttonTextColor = textColor

				document.documentElement.style.setProperty("--main-header-button-color", color)
				document.documentElement.style.setProperty("--main-header-button-text-color", textColor)

				// Apply button color and text color to header buttons in preview
				const previewArea = document.querySelector(".main-layout-preview")
				if (previewArea) {
					const headerButtons = previewArea.querySelectorAll(".logo-area button:not(.color-picker-btn)")
					headerButtons.forEach((element) => {
						const htmlElement = element as HTMLElement
						htmlElement.style.setProperty("background-color", color, "important")
						htmlElement.style.setProperty("color", textColor, "important")
						htmlElement.style.setProperty("border-color", color, "important")
					})
				}
			} else if (mode === "underHeader") {
				semantic.menuUp.borderBottom = color
				localSemantic.menuUp.borderBottom = color
				document.documentElement.style.setProperty("--main-header-borderbottom-color", color)
				// Apply to preview area immediately
				const previewArea = document.querySelector(".main-layout-preview") as HTMLElement
				if (previewArea) {
					previewArea.style.setProperty("--main-header-borderbottom-color", color)
					const logoArea = previewArea.querySelector(".logo-area") as HTMLElement
					if (logoArea) {
						logoArea.style.setProperty("border-bottom-color", color, "important")
					}
				}
			} else {
				semantic.menuUp.backgroundColor = color
				localSemantic.menuUp.backgroundColor = color
				document.documentElement.style.setProperty("--main-header-background-color", color)
				// Apply background color to logo-area in preview
				const previewArea = document.querySelector(".main-layout-preview")
				if (previewArea) {
					const logoArea = previewArea.querySelector(".logo-area") as HTMLElement
					if (logoArea) {
						logoArea.style.setProperty("background-color", color, "important")
					}
				}
			}
		},
	}

	const match = Object.keys(colorMappings).find((key) => classes.includes(key.replace("--", "")))
	if (!match) {
		console.warn("No matching style variable found for classes:", classes)
		return
	}

	const updateFunction = colorMappings[match]
	if (updateFunction) {
		updateFunction()
	}
	console.log("🍞 Emitting updateTheme with breadcrumb:", localThemeState.value.selectedTheme?.components?.breadcrumb)
	emit("updateTheme", localThemeState.value)
}

// Handle hex input changes
const onHexInputChange = () => {
	let value = hexInputValue.value.replace(/[^#0-9A-Fa-f]/g, "")

	if (value && !value.startsWith("#")) {
		value = "#" + value
	}

	if (value.length > 7) {
		value = value.substring(0, 7)
	}

	if (hexInputValue.value !== value) {
		hexInputValue.value = value
	}
}

const validateAndApplyHex = () => {
	if (isValidHexColor(hexInputValue.value)) {
		if (colorMode.value === "text") {
			selectedTextColor.value = hexInputValue.value
		} else {
			selectedColor.value = hexInputValue.value
		}

		// Apply auto contrast if enabled
		if (autoContrast.value && colorMode.value === "background" && clickedElement) {
			const textColor = isColorLight(hexInputValue.value) ? "#000000" : "#ffffff"
			selectedTextColor.value = textColor
			// Auto-apply text color for better contrast
			const originalMode = colorMode.value
			colorMode.value = "text"

			// Apply text color
			updateLocalThemeSettings(Array.from(clickedElement.classList), textColor, "text")
			clickedElement.style.color = textColor

			// Switch back to original mode
			colorMode.value = originalMode
		}

		applyColor()
	} else {
		hexInputValue.value = colorMode.value === "text" ? selectedTextColor.value : selectedColor.value
	}
}

const isValidHexColor = (hex: string): boolean => {
	return /^#[0-9A-Fa-f]{6}$/.test(hex)
}

const sendColorToParent = () => {
	const currentColor = colorMode.value === "text" ? selectedTextColor.value : selectedColor.value
	if (isValidHexColor(currentColor)) {
		emit("colorSelected", currentColor)
		closePicker()
	}
}

const rgbToHex = (rgb: string) => {
	if (rgb.startsWith("#")) {
		return rgb.length === 7 ? rgb : "#ffffff"
	}

	const result = rgb.match(/\d+/g)
	if (!result || result.length < 3) {
		console.warn("Cannot parse color:", rgb)
		return "#ffffff"
	}

	return (
		"#" +
		result
			.slice(0, 3)
			.map((x) => {
				const num = parseInt(x)
				return num.toString(16).padStart(2, "0")
			})
			.join("")
	)
}

// Image upload functions
const triggerHeaderLogoUpload = () => {
	fileInputHeaderLogo.value?.click()
}

const removeHeaderLogoImage = () => {
	headerLogoImage.value = null
	localThemeState.value.templateUI!.menulogoImageBinary = undefined
	localThemeState.value.templateUI!.menulogoImageContentType = undefined
	emit("updateTheme", localThemeState.value)
}

const handleHeaderLogoUpload = async (event: Event) => {
	const target = event.target as HTMLInputElement
	const file = target.files?.[0]

	if (!file) return

	if (!file.type.startsWith("image/")) {
		console.error("Invalid file type. Please select an image file.")
		return
	}

	// Clear previous content immediately
	headerLogoImage.value = null
	spinnerHeaderLogo.value = true

	try {
		// Handle SVG files as text, others as binary
		const reader = new FileReader()
		reader.onload = (e) => {
			let base64String = ""

			if (file.type === "image/svg+xml" || file.type.includes("svg")) {
				// For SVG, read as text and encode to base64
				const svgContent = e.target?.result as string
				base64String = btoa(svgContent)
			} else {
				// For other images, read as ArrayBuffer
				const arrayBuffer = e.target?.result as ArrayBuffer
				const uint8Array = new Uint8Array(arrayBuffer)
				let binaryString = ""
				for (let i = 0; i < uint8Array.length; i++) {
					binaryString += String.fromCharCode(uint8Array[i])
				}
				base64String = btoa(binaryString)
			}

			// Store binary data and content type
			localThemeState.value.templateUI!.menulogoImageBinary = base64String
			localThemeState.value.templateUI!.menulogoImageContentType = file.type

			// Create display image
			headerLogoImage.value = createImageFromBinary(base64String, file.type)

			emit("updateTheme", localThemeState.value)
			spinnerHeaderLogo.value = false
		}
		reader.onerror = () => {
			console.error("Error reading header logo file")
			alert("Failed to load header logo. Please try again.")
			headerLogoImage.value = null
			spinnerHeaderLogo.value = false
		}

		// Read file appropriately based on type
		if (file.type === "image/svg+xml" || file.type.includes("svg")) {
			reader.readAsText(file)
		} else {
			reader.readAsArrayBuffer(file)
		}
	} catch (error: any) {
		console.error("Error loading header logo:", error)
		alert(error.message || "Failed to load header logo. Please try again.")
		headerLogoImage.value = null
		spinnerHeaderLogo.value = false
	} finally {
		// Clear the input to allow re-uploading the same file
		target.value = ""
	}
}

watch(
	() => props.selectedTemplate,
	() => {
		if (props.selectedTemplate!.selectedTheme !== undefined) applyTemplate(props.selectedTemplate!)
	},
	{ immediate: true }
)

// Watch selectedColor to update hexInputValue
watch(selectedColor, (newColor) => {
	if (colorMode.value === "background" && newColor) {
		hexInputValue.value = newColor.startsWith("#") ? newColor : "#" + newColor
	}
})

// Watch selectedTextColor to update hexInputValue
watch(selectedTextColor, (newColor) => {
	if (colorMode.value === "text" && newColor) {
		hexInputValue.value = newColor.startsWith("#") ? newColor : "#" + newColor
	}
})

// Watch colorMode to update hexInputValue when switching modes
watch(colorMode, (newMode) => {
	const currentColor = newMode === "background" ? selectedColor.value : selectedTextColor.value
	if (currentColor) {
		hexInputValue.value = currentColor.startsWith("#") ? currentColor : "#" + currentColor
	}
})

watch(
	() => hexInputValue.value,
	(newHex) => {
		if (isValidHexColor(newHex)) {
			if (colorMode.value === "text" && newHex !== selectedTextColor.value) {
				selectedTextColor.value = newHex
				// Apply color change immediately
				onColorPickerChange(newHex)
			} else if (colorMode.value === "background" && newHex !== selectedColor.value) {
				selectedColor.value = newHex
				// Apply color change immediately
				onColorPickerChange(newHex)
			} else if (colorMode.value === "buttonColor" && newHex !== selectedTextColor.value) {
				selectedTextColor.value = newHex
				// Apply color change immediately
				onColorPickerChange(newHex)
			} else if (colorMode.value === "underHeader" && newHex !== selectedColor.value) {
				selectedColor.value = newHex
				// Apply color change immediately
				onColorPickerChange(newHex)
			}
		}
	}
)
</script>

<style lang="scss" scoped>
/* Update header-logo-wrapper to be relative for positioning */
.header-logo-wrapper {
	position: relative;
}

.main-layout-preview {
	border: 1px solid #e9ecef;
	border-radius: 8px;
	overflow: hidden;
	background: #fff;
	box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

/* Header Styles */
.logo-area {
	height: 60px;
	background-color: var(--main-header-background-color);
	color: var(--main-header-text-color, #333333);
	border-bottom: 0.2em solid var(--main-header-borderbottom-color);
	padding: 0 1rem;
	display: flex;
	align-items: center;
	position: relative;
}

.default-logo {
	height: 100%;
	display: flex;
	align-items: center;
}

.default-logo :deep(img) {
	height: 100%;
	width: auto;
	object-fit: contain;
}

.logo-image-container :deep(img) {
	height: calc(60px - 0.5rem);
	width: auto;
	object-fit: contain;
}

/* Header buttons in logo area */
.logo-area :deep(button:not(.color-picker-btn)) {
	background: var(--main-header-button-color, #007bff) !important;
	color: var(--main-header-button-text-color, white) !important;
	transition: all 0.3s ease;
}

.logo-area :deep(button:not(.color-picker-btn)):hover {
	filter: brightness(1.1);
}

.header-button {
	background: var(--main-header-button-color, #007bff);
	color: white;
	border: none;
	padding: 0.5rem 1rem;
	border-radius: 4px;
	font-size: 0.875rem;
	cursor: pointer;
	transition: background-color 0.3s ease;
}

.header-button:hover {
	background: var(--main-header-button-color, #0056b3);
	filter: brightness(0.9);
}

.user-button {
	background: #6c757d;
	color: white;
	border: none;
	padding: 0.5rem 1rem;
	border-radius: 4px;
	font-size: 0.875rem;
	cursor: pointer;
	display: flex;
	align-items: center;
	gap: 0.5rem;
}

.progress-bar-mock {
	font-size: 0.75rem;
	color: #666;
	padding: 0.25rem 0.5rem;
	background: #e9ecef;
	border-radius: 4px;
}

/* Main Container */
.main-container {
	display: flex;
	height: 600px;
}

/* Sidebar Styles */
.sidebar-preview {
	width: 240px;
	transition: all 0.3s ease;
	background-color: var(--main-sidebar-background-color, #1a1a2e);
	height: 100%;
	flex-shrink: 0;
}

.sidebar-preview.collapsed {
	width: 60px;
}

/* Right Content */
.right-content {
	flex: 1;
	display: flex;
	flex-direction: column;
	overflow: hidden;
}

.sidebar-content {
	height: 100%;
	display: flex;
	flex-direction: column;
	padding: 1rem 0;
}

.menu-toggle {
	background: none;
	border: none;
	color: var(--main-sidebar-text-color, #ffffff);
	font-size: 1.25rem;
	padding: 0.75rem;
	cursor: pointer;
	display: flex;
	justify-content: flex-end;
	width: 100%;
}

.navigation {
	flex: 1;
	padding: 1rem 0;
}

.nav-item {
	display: flex;
	align-items: center;
	padding: 0.75rem 1rem;
	background: var(--main-sidebar-button-color, transparent);
	border: none;
	color: var(--main-sidebar-button-text-color, var(--main-sidebar-text-color, #ffffff));
	cursor: pointer;
	transition: all 0.3s ease;
	width: 100%;
	text-align: left;
}

.nav-item:hover {
	background: var(--main-sidebar-button-color, rgba(255, 255, 255, 0.1));
	filter: brightness(1.1);
}

.nav-item.active {
	background: var(--main-sidebar-active-color, #007bff);
	color: white;
}

.nav-icon {
	width: 1.25rem;
	margin-right: 0.75rem;
	flex-shrink: 0;
}

.nav-text {
	font-size: 0.875rem;
	transition: opacity 0.3s ease;
}

.nav-text.hide {
	opacity: 0;
}

.user-section {
	border-top: 1px solid rgba(255, 255, 255, 0.2);
	padding: 1rem;
	transition: opacity 0.3s ease;
}

.user-section.hide {
	opacity: 0;
}

.user-info {
	display: flex;
	flex-direction: column;
	gap: 0.5rem;
}

.user-name {
	color: var(--main-sidebar-text-color, #ffffff);
	text-decoration: none;
	font-size: 0.875rem;
	font-weight: 500;
}

.logout-link {
	color: rgba(255, 255, 255, 0.7);
	text-decoration: none;
	font-size: 0.75rem;
}

.logout-link:hover {
	color: var(--main-sidebar-text-color, #ffffff);
}

/* Content Area */
.content-preview {
	flex: 1;
	background: #f8f9fa;
	display: flex;
	flex-direction: column;
	overflow: auto;
}

.breadcrumb-area {
	position: relative;
	padding: 1rem;
	background: var(--main-breadcrumb-background-color, white);
	border-bottom: 1px solid #e9ecef;
	font-size: 0.875rem;
	color: var(--main-breadcrumb-text-color, #6c757d);
}

.main-layout-preview .breadcrumb-area i {
	color: var(--main-breadcrumb-icon-color, var(--main-breadcrumb-text-color, #6c757d)) !important;
}

.main-layout-preview .breadcrumb-area .breadcrumb-separator {
	color: var(--main-breadcrumb-text-color, #6c757d) !important;
	margin: 0 0.5rem;
}

.page-content {
	flex: 1;
	padding: 2rem;
}

.page-content h2 {
	margin: 0 0 1rem 0;
	color: #333;
}

.page-content p {
	color: #666;
	line-height: 1.5;
}

/* Color Picker Panel - matching Components file structure */
.color-picker-panel {
	background: white;
	border-radius: 12px;
	box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
	border: 1px solid #e0e0e0;
	min-width: 300px;
	overflow: hidden;
}

.color-picker-header {
	display: flex;
	justify-content: space-between;
	align-items: center;
	padding: 12px 16px;
	background: #f8f9fa;
	border-bottom: 1px solid #e0e0e0;
}

.color-picker-title {
	font-weight: 600;
	color: #333;
	font-size: 14px;
}

.color-picker-close {
	background: none;
	border: none;
	font-size: 20px;
	color: #666;
	cursor: pointer;
	padding: 0;
	width: 24px;
	height: 24px;
	display: flex;
	align-items: center;
	justify-content: center;
	border-radius: 4px;
	transition: all 0.2s ease;
}

.color-picker-close:hover {
	background: #e9ecef;
	color: #333;
}

/* Mode tabs */
.mode-tabs {
	display: flex;
	flex-wrap: wrap;
	background: #f8f9fa;
	border-bottom: 1px solid #e0e0e0;
	padding: 8px;
	gap: 4px;
}

.mode-tab {
	flex: 1 1 auto;
	min-width: 90px;
	padding: 8px 12px !important;
	font-size: 12px !important;
	border-radius: 4px !important;
	background: transparent !important;
	border: 1px solid transparent !important;
	color: #666 !important;
	transition: all 0.2s ease !important;
	display: flex !important;
	align-items: center !important;
	justify-content: center !important;
	gap: 4px !important;
	white-space: nowrap;
}

.mode-tab:hover {
	background: rgba(255, 255, 255, 0.7) !important;
	color: #333 !important;
}

.mode-tab.active {
	background: white !important;
	border-color: #007bff !important;
	color: #007bff !important;
	font-weight: 500 !important;
	box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1) !important;
}

/* Auto contrast section */
.auto-contrast-section {
	padding: 0;
	margin: 0;
}

.auto-contrast-section .flex {
	display: flex;
	align-items: center;
	gap: 8px;
	padding: 12px 16px;
	border-top: 1px solid #e0e0e0;
	border-bottom: 1px solid #e0e0e0;
	background: #f8f9fa;
}

.auto-contrast-section .text-sm {
	font-size: 13px;
	color: #495057;
	cursor: pointer;
	user-select: none;
}

.auto-contrast-section .text-gray-400 {
	color: #6c757d;
	font-size: 14px;
}

/* Color picker content */
.color-picker-content {
	padding: 16px;
	display: flex;
	gap: 16px;
	align-items: flex-start;
}

.color-picker {
	border: none !important;
	box-shadow: none !important;
}

/* Color palette */
.color-palette-miniatures {
	flex-shrink: 0;
	width: 80px;
}

.palette-grid {
	display: flex;
	flex-direction: column;
	gap: 8px;
}

.primary-colors-box {
	border: 2px solid #000000;
	border-radius: 4px;
	padding: 4px;
	margin-bottom: 6px;
}

.palette-row {
	display: flex;
	gap: 4px;
	justify-content: space-between;
}

.palette-swatch {
	width: 24px;
	height: 24px;
	border-radius: 4px;
	cursor: pointer;
	border: 1px solid #e0e0e0;
	transition: all 0.2s ease;
}

.palette-swatch:hover {
	transform: scale(1.1);
	box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}

.primary-swatch {
	border: 2px solid #000000 !important;
	font-weight: bold;
}

.primary-row .palette-swatch {
	position: relative;
}

.hex-input-container {
	display: flex;
	align-items: center;
	gap: 8px;
	padding: 12px 16px;
	border-top: 1px solid #e0e0e0;
	background: #f8f9fa;
}

.hex-input-label {
	font-size: 12px;
	font-weight: 500;
	color: #666;
	min-width: 30px;
}

.hex-input {
	flex: 1;
	font-family: monospace;
	font-size: 12px;
	text-transform: uppercase;
}

.send-color-btn {
	min-width: 32px !important;
	height: 32px !important;
	padding: 0 !important;
	border-radius: 4px !important;
	background: #007bff !important;
	color: white !important;
	border: none !important;
	box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1) !important;
	transition: all 0.2s ease !important;
}

.send-color-btn:hover {
	background: #0056b3 !important;
	transform: translateY(-1px) !important;
	box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15) !important;
}

/* Color picker button styles */
.color-picker-btn-container {
	position: absolute;
	top: 10px;
	right: 10px;
	z-index: 1000;
}

.color-picker-btn {
	padding: 0.5rem !important;
	border-radius: 50% !important;
	width: 36px !important;
	height: 36px !important;
	display: flex !important;
	align-items: center !important;
	justify-content: center !important;
	background: rgba(255, 255, 255, 0.9) !important;
	border: 1px solid rgba(0, 0, 0, 0.1) !important;
	box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1) !important;
	transition: all 0.3s ease !important;
}

.color-picker-btn:hover {
	background: rgba(255, 255, 255, 1) !important;
	transform: scale(1.1) !important;
	box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15) !important;
}

.color-picker-btn .pi-palette {
	font-size: 1rem;
	color: #000000 !important;
}

/* Override PrimeVue Button secondary severity styles */
.p-button.color-picker-btn .pi-palette,
.p-button-secondary.color-picker-btn .pi-palette,
button.color-picker-btn .pi-palette {
	color: #000000 !important;
}

.color-picker-btn.active {
	background: #6366f1 !important;
	border-color: #4f46e5 !important;
}

.color-picker-btn.active .pi-palette {
	color: white !important;
}

/* Ensure white icon when active, even with PrimeVue classes */
.p-button.color-picker-btn.active .pi-palette,
.p-button-secondary.color-picker-btn.active .pi-palette,
button.color-picker-btn.active .pi-palette {
	color: white !important;
}

/* Specific positioning for different sections */
.sidebar-picker {
	top: 20px;
	left: 20px;
	right: auto;
}

.content-picker {
	top: 20px;
	right: 20px;
}

/* Make sections relatively positioned for proper button placement */
.logo-area {
	position: relative;
}

.sidebar-preview {
	position: relative;
}

.content-preview {
	position: relative;
}

/* Header logo container styles */
.header-logo-wrapper {
	position: relative;
	min-height: 60px;
	display: flex;
	align-items: center;
}

.header-logo-container {
	position: relative;
	width: 100%;
	max-width: 400px;
	height: 60px;
	display: flex;
	align-items: center;
	overflow: hidden;
}

.logo-content {
	width: 100%;
	height: 100%;
	display: flex;
	align-items: center;
	position: relative;
}

.logo-image-container {
	width: 100%;
	height: 100%;
	display: flex;
	align-items: center;
	justify-content: flex-start;
}

/* Ensure all images fit within the header area */
.header-logo-container .logo-image-container img,
.header-logo-img,
.default-logo {
	max-width: 100%;
	max-height: 60px !important;
	height: auto !important;
	width: auto !important;
	object-fit: contain !important;
	display: block;
}

.header-logo-container .logo-image-container svg,
.header-logo-svg {
	max-width: 100%;
	max-height: 60px !important;
	height: auto !important;
	width: auto !important;
	object-fit: contain !important;
	display: block;
}

/* Styles for transparent images in header */
.header-logo-container .transparent-image,
.header-logo-img {
	background: transparent !important;
	object-fit: contain !important;
	box-shadow: none;
	border: none;
}

/* Container styles to support transparency */
.header-logo-container .logo-content {
	background: transparent !important;
	background-image: none !important;
	background-color: transparent !important;
}

/* Styles for transparent images in header */
.header-logo-container .transparent-image,
.header-logo-img {
	background: transparent !important;
	object-fit: contain !important;
	box-shadow: none;
	border: none;
}

/* Upload Controls Section */
.upload-controls-section {
	display: flex;
	margin-bottom: 1rem;
	padding: 1rem;
	background: #f8f9fa;
	border-radius: 8px;
	border: 1px solid #e9ecef;
}

.header-logo-controls {
	display: flex;
	align-items: center;
	gap: 0.5rem;
}

.control-label {
	font-size: 0.875rem;
	font-weight: 500;
	color: #495057;
	min-width: 80px;
}

.control-btn {
	background: rgba(50, 21, 69, 0.9) !important;
	color: white !important;
	border: none !important;
	padding: 0.375rem 0.75rem !important;
	border-radius: 4px !important;
	font-size: 0.75rem !important;
	cursor: pointer !important;
	transition: all 0.3s ease !important;
	box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1) !important;
	display: flex !important;
	align-items: center !important;
	gap: 0.25rem !important;
}

.control-btn:hover {
	background: rgba(50, 21, 69, 1) !important;
	transform: translateY(-1px) !important;
	box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15) !important;
}

.control-btn.remove-btn {
	background: rgba(220, 53, 69, 0.9) !important;
}

.control-btn.remove-btn:hover {
	background: rgba(200, 35, 51, 1) !important;
}

.control-btn i {
	font-size: 0.75rem !important;
}
</style>
